import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faSpinner } from "@fortawesome/free-solid-svg-icons";
import "../../styles/produs/Comments.css";
import { format } from "date-fns";
import api from "../../api/http";

const ProductComments = ({ productId }) => {
  const [comments, setComments] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const intervalRef = useRef(null);
  const isMountedRef = useRef(false);

  const normalizeList = (data) => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  };

  const makeAvatarUrl = (avatar) => {
    if (!avatar || typeof avatar !== "string") return "";
    if (avatar.startsWith("http://") || avatar.startsWith("https://")) return avatar;

    const base = api?.defaults?.baseURL || "";
    if (!base) return avatar;

    if (base.endsWith("/") && avatar.startsWith("/")) return base.slice(0, -1) + avatar;
    if (!base.endsWith("/") && !avatar.startsWith("/")) return base + "/" + avatar;
    return base + avatar;
  };

  // NUME doar din first_name + last_name
  const displayName = (u) => {
    const first = String(u?.first_name || "").trim();
    const last = String(u?.last_name || "").trim();
    const full = `${first} ${last}`.trim();
    return full ? full : "Unknown User";
  };

  // backend: "2026-02-21 14:50:14" -> Date
  const parseBackendDate = (v) => {
    if (!v) return null;
    const s = String(v).trim().replace(" ", "T");
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fetchUserInfo = async (userId) => {
    try {
      const res = await api.get(`/users/profiles/${userId}/`);
      return res.data;
    } catch (e) {
      console.error("Error fetching user info:", e);
      return null; // cache null ca să nu refacem request la infinit
    }
  };

  const fetchComments = async ({ silent = false } = {}) => {
    if (!productId) return;

    if (!silent) {
      setLoading(true);
      setError("");
    }

    try {
      const res = await api.get(`/produs/comments-list/${productId}`);
      const list = normalizeList(res.data);
      setComments(list);
    } catch (e) {
      console.error("Error fetching comments:", e);
      setComments([]);
      setError("Nu am putut încărca comentariile.");
    } finally {
      setLoading(false);
    }
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startInterval = () => {
    stopInterval();
    intervalRef.current = setInterval(() => fetchComments({ silent: true }), 5000);
  };

  // init + cleanup + focus/blur
  useEffect(() => {
    isMountedRef.current = true;

    setLoading(true);
    setError("");
    setUserDetails({}); // reset cache când schimbi produsul
    fetchComments({ silent: false });
    startInterval();

    const onFocus = () => startInterval();
    const onBlur = () => stopInterval();

    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      stopInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // fetch user profiles doar pentru user_id lipsă
  useEffect(() => {
    const fetchMissingUsers = async () => {
      const ids = Array.from(new Set(comments.map((c) => c.user_id).filter(Boolean)));
      const missing = ids.filter((id) => !(id in userDetails));
      if (!missing.length) return;

      const updates = {};
      await Promise.all(
        missing.map(async (id) => {
          updates[id] = await fetchUserInfo(id);
        })
      );

      if (!isMountedRef.current) return;
      setUserDetails((prev) => ({ ...prev, ...updates }));
    };

    if (comments.length) fetchMissingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  // sortare: cel mai nou primul
  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      const da = parseBackendDate(a.created_at)?.getTime() ?? 0;
      const db = parseBackendDate(b.created_at)?.getTime() ?? 0;
      return db - da;
    });
  }, [comments]);

  const hasComments = sortedComments.length > 0;

  return (
    <div className="srComments">
      <div className="srComments_head">
        <h3 className="srComments_title">Comentarii</h3>

      </div>

      {loading ? (
        <div className="srComments_state">
          <div className="srComments_loader" />
          <div className="srComments_stateText">Se încarcă…</div>
        </div>
      ) : error ? (
        <div className="srComments_error">{error}</div>
      ) : !hasComments ? (
        <div className="srComments_empty">Nu există comentarii pentru acest produs.</div>
      ) : (
        <ul className="srComments_list">
          {sortedComments.map((comment) => {
            const uRaw = userDetails[comment.user_id] || null;
            const name = displayName(uRaw);
            const avatar = makeAvatarUrl(uRaw?.avatar);
            const d = parseBackendDate(comment.created_at);

            return (
              <li key={comment.id} className="srComments_item">
                <div className="srComments_avatarWrap" aria-hidden="true">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt=""
                      className="srComments_avatarImg"
                      loading="lazy"
                    />
                  ) : (
                    <FontAwesomeIcon icon={faUser} className="srComments_avatarIcon" />
                  )}
                </div>

                <div className="srComments_body">
                  <div className="srComments_topRow">
                    <div className="srComments_name">{name}</div>
                    <div className="srComments_date">
                      {d ? format(d, "dd.MM.yyyy HH:mm") : ""}
                    </div>
                  </div>

                  <div className="srComments_text">{comment.comment}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProductComments;